// pages/Facturacion.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Table,
  Typography,
  Space,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Divider,
  message,
  Select,
  DatePicker,
  Alert,
  Descriptions,
  List,
  Popover,
  Image
} from 'antd';
import {
  FileTextOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  CalculatorOutlined,
  HomeOutlined,
  UserOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  BankOutlined,
  TagOutlined,
  PercentageOutlined,
  BarcodeOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  FileDoneOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

// Logo por defecto (puedes reemplazarlo con el logo real del hotel)
const defaultLogo = '/logo-hotel.png';

const Facturacion = ({ reserva, visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [factura, setFactura] = useState(null);
  const [tipoDocumento, setTipoDocumento] = useState('proforma');
  const [fechaFactura, setFechaFactura] = useState(dayjs());
  const [generando, setGenerando] = useState(false);
  const [historialFacturas, setHistorialFacturas] = useState([]);
  const [datosHotel, setDatosHotel] = useState(null);

  useEffect(() => {
    if (visible && reserva?.NumReserva) {
      cargarHistorialFacturas();
      generarDocumento('proforma');
      cargarDatosHotel();
    }
  }, [visible, reserva]);

  const cargarHistorialFacturas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/facturacion/reserva/${reserva.NumReserva}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistorialFacturas(response.data.data || []);
    } catch (error) {
      console.error('Error cargando historial de facturas:', error);
    }
  };

  const cargarDatosHotel = async () => {
    try {
      const token = localStorage.getItem('token');
      // Asumiendo que hay un endpoint para obtener datos del hotel
      const response = await axios.get(`${BASE_URL}/api/opciones/hotel`,
        { headers: { Authorization: `Bearer ${token}` } }
      );      
      console.log(response.data.data[0]);
      setDatosHotel(response.data.data[0] || {});
    } catch (error) {
      console.log('Error cargando datos del hotel:', error);
      // Datos de ejemplo por si falla
      setDatosHotel({
        nombre: '',
        direccion: '',
        cp: '',
        poblacion: '',
        provincia: '',
        telefono: '',
        email: '',
        web: '',
        nif: '',
        logo: defaultLogo
      });
    }
  };

  const generarDocumento = async (tipo = 'proforma') => {
    setGenerando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/facturacion/generar/${reserva.NumReserva}`,
        {
          tipo: tipo,
          fechaFactura: fechaFactura.toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log(" Factura => ", response.data.data);
      
      setFactura(response.data.data);
      setTipoDocumento(tipo);
      
      if (tipo === 'factura') {
        message.success('Factura generada correctamente');
        cargarHistorialFacturas();
      } else {
        message.success('Proforma generada correctamente');
      }
    } catch (error) {
      console.error('Error generando documento:', error);
      message.error(error.response?.data?.error || 'Error generando documento');
    } finally {
      setGenerando(false);
    }
  };

  // Columnas para el detalle de líneas (estilo FastReport)
  const columnsDetalle = [
    {
      title: 'Hab.',
      dataIndex: 'habitacion',
      key: 'habitacion',
      width: 35,
      align: 'center',
      render: (val) => val || '-'
    },
    {
      title: 'Línea',
      dataIndex: 'Linea',
      key: 'Linea',
      width: 35,
      align: 'center'
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 100,
      render: (text, record) => (
        <span>
          {text}
          {record.observaciones && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
              {record.observaciones}
            </div>
          )}
        </span>
      )
    },
    {
      title: 'Localizador',
      dataIndex: 'snumero',
      key: 'snumero',
      width: 75,
      render: (val) => val || '-'
    },
    {
      title: 'Tipo',
      dataIndex: 'tipofac',
      key: 'tipofac',
      width: 50,
      align: 'center',
      render: (tipo) => {
        if (!tipo) return <Tag size="small">EST</Tag>;
        if (tipo === 'CAR' || tipo === 'CAK') return <Tag color="orange" size="small">CAR</Tag>;
        if (tipo === 'TAS') return <Tag color="gold" size="small">TAS</Tag>;
        return tipo;
      }
    },
    {
      title: 'Entrada',
      dataIndex: 'fechaent',
      key: 'fechaent',
      width: 75,
      align: 'center',
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY')
    },
    {
      title: 'Salida',
      dataIndex: 'fechasal',
      key: 'fechasal',
      width: 75,
      align: 'center',
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY')
    },
    {
      title: 'Régimen',
      dataIndex: 'regimen',
      key: 'regimen',
      width: 20,
      align: 'center',
      render: (reg) => reg || '-'
    },
    {
      title: 'Precio',
      dataIndex: 'precio',
      key: 'precio',
      width: 60,
      align: 'right',
      render: (valor) => `${parseFloat(valor || 0).toFixed(2)} €`
    },
    {
      title: 'Noches',
      dataIndex: 'diascant',
      key: 'diascant',
      width: 30,
      align: 'right',
      render: (val) => val || 0
    },
    {
      title: 'PAX',
      dataIndex: 'nagrupa',
      key: 'nagrupa',
      width: 50,
      align: 'center',
      render: (pax) => pax || 1
    },
{
    title: 'Importe',
    dataIndex: 'importe',
    key: 'importe',
    width: 110,
    align: 'right',
    render: (valor, record) => {
      // El importe ya viene calculado del backend
      // Si queremos asegurarnos, podemos calcularlo también:
      const importeCalculado = (record.precio || 0) * (record.diascant || 0) * (record.nagrupa || 1);
      return (
        <span>
          {/* <Text strong>{parseFloat(valor || importeCalculado || 0).toFixed(2)} €</Text> */}
          <Text strong>{parseFloat(importeCalculado || 0).toFixed(2)} €</Text>
          {record.descuentos?.total > 0 && (
            <div style={{ fontSize: 10, color: '#52c41a' }}>
              (Desc: {record.descuentos.total.toFixed(2)} €)
            </div>
          )}
        </span>
      );
    }
  }
  ];

  // Función para agrupar líneas por IVA (como en FastReport)
  const agruparPorIVA = (lineas) => {
    const grupos = {};
    
    lineas.forEach(linea => {
      // Determinar si es incluido o desglosado
      //const aplicado = linea.tipofac === 'CAR' || linea.tipofac === 'CAK' || linea.tipofac === 'TAS' ? 'D' : 'I';
      const aplicado = linea.ivaAplicado;
      const key = `${linea.valoriva}-${aplicado}`;
      
      if (!grupos[key]) {
        grupos[key] = {
          valoriva: linea.valoriva,
          aplicado: aplicado,
          base: 0,
          iva: 0,
          total: 0,
          lineas: []
        };
      }
      
      grupos[key].base += linea.importebase || 0;
      grupos[key].iva += linea.importeiva || 0;
      grupos[key].total += linea.importetotal || 0;
      grupos[key].lineas.push(linea);
    });
    
    return Object.values(grupos);
  };

  const handleEmitirFactura = () => {
    Modal.confirm({
      title: 'Realizar factura',
      content: '¿Estás seguro de realizar la factura?',
      okText: 'Realizar factura',
      cancelText: 'Cancelar',
      onOk: () => generarDocumento('factura')
    });
  };

  const contenidoFactura = factura && (
    <>
      {/* Cabecera estilo FastReport */}
      <Card style={{ marginBottom: 16, padding: '16px' }} bordered={false}>
        <Row gutter={[16, 16]} align="top">
          {/* Logo */}
          <Col xs={24} sm={8} md={6}>
            <div style={{ textAlign: 'left' }}>
              <img 
                src={datosHotel?.logo || defaultLogo} 
                alt="Logo Hotel" 
                style={{ maxWidth: '100%', maxHeight: '100px' }}
              />
            </div>
          </Col>
          
          {/* Datos del hotel */}
          <Col xs={24} sm={12} md={12}>
            <Title level={3} style={{ margin: 0 }}>{datosHotel?.Nombre || 'HOTEL'}</Title>
            <Text><EnvironmentOutlined /> {datosHotel?.Direccion || ''}</Text><br />
            <Text>{datosHotel?.cp || ''} {datosHotel?.Poblacion || ''} {datosHotel?.Provincia || ''}</Text><br />
            <Text><PhoneOutlined /> {datosHotel?.Telefono || ''}</Text><br />
            <Text><MailOutlined /> {datosHotel?.Email || ''}</Text><br />
            <Text><GlobalOutlined /> {datosHotel?.PaginaWeb || ''}</Text><br />
            <Text type="secondary">NIF: {datosHotel?.NIF || ''}</Text>
          </Col>
          
          {/* Fecha y número */}
          <Col xs={24} sm={4} md={6} style={{ textAlign: 'right' }}>
            <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
              <Text strong style={{ fontSize: 16 }}>
                {tipoDocumento === 'factura' ? 'FACTURA' : 'PROFORMA'}
              </Text>
              <br />
              <Text>
                {factura.numeracion && `${factura.numeracion.serie}-${factura.numeracion.numero}`}
              </Text>
              <br />
              <Text type="secondary">Fecha: {dayjs(factura.fechaEmision).format('DD/MM/YYYY')}</Text>
              <br />
              <Text type="secondary">Página: 1</Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Datos de agencia y reserva */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Text type="secondary">Agencia:</Text>
            <br />
            <Text strong>{factura.datosAgencia?.Nombre || 'No especificada'}</Text>
            {factura.datosAgencia?.NIF && (
              <>
                <br />
                <Text type="secondary">NIF: {factura.datosAgencia.NIF}</Text>
              </>
            )}
          </Col>
          <Col span={8}>
            <Text type="secondary">Subagencia:</Text>
            <br />
            <Text>{factura.datosAgencia?.Subagencia || '-'}</Text>
          </Col>
          <Col span={8}>
            <Text type="secondary">Nº Reserva:</Text>
            <br />
            <Text strong>{factura.numeroReserva}</Text>
            <br />
            <Text type="secondary">Cliente: {factura.datosCliente?.nombre}</Text>
          </Col>
        </Row>
      </Card>

      {/* Tabla de detalle (estilo FastReport) */}
      <Card title="Detalle" size="small" style={{ marginBottom: 16 }}>
        <Table
          dataSource={factura.lineas || []}
          columns={columnsDetalle}
          pagination={false}
          size="small"
          rowKey={(record, index) => `${record.Linea}-${record.fechaent}-${index}`}
          scroll={{ x: 1500 }}
          bordered
          summary={() => {
            const totalBase = (factura.lineas || []).reduce((sum, item) => sum + (item.importebase || 0), 0);
            const totalIVA = (factura.lineas || []).reduce((sum, item) => sum + (item.importeiva || 0), 0);
            const totalOriginal = (factura.lineas || []).reduce((sum, item) => sum + (item.importe || 0), 0);
            const totalConDescuentos = (factura.lineas || []).reduce((sum, item) => sum + (item.importetotalconiva || item.importe || 0), 0);
            const totalDescuentos = (factura.lineas || []).reduce((sum, item) => sum + (item.descuentos?.total || 0), 0);
            
            return (
              <>
                {totalDescuentos > 0 && (
                        <Table.Summary.Row style={{ background: '#fff7e6' }}>
                          <Table.Summary.Cell index={0} colSpan={11} align="right">
                            <Text type="warning">Total Descuentos Aplicados:</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text type="warning">- {totalDescuentos.toFixed(2)} €</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}             
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={11} align="right">
                    <Text strong>BASES IMPONIBLES</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>{totalBase.toFixed(2)} €</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                
                {/* Aquí podríamos agregar el desglose por IVA como en FastReport */}
                {agruparPorIVA(factura.lineas || []).map((grupo, idx) => (
                  <Table.Summary.Row key={idx}>
                    <Table.Summary.Cell index={0} colSpan={10} align="right">
                      <Text>
                        {grupo.aplicado === 'I' ? 'I.V.A. Incluido' : 'I.V.A. Desglosado'} {grupo.valoriva}%
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text>{grupo.base.toFixed(2)} €</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text>{grupo.iva.toFixed(2)} €</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ))}
              </>
            );
          }}
        />
      </Card>

      {/* Totales estilo FastReport */}
<Row gutter={16} justify="end" style={{ marginBottom: 16 }}>
  <Col span={8}>
    <Card size="small">
      <Statistic 
        title="Base Imponible (después dtos)" 
        value={factura.resumen?.totalBaseImponible || 0} 
        precision={2} 
        suffix="€" 
      />
    </Card>
  </Col>
  <Col span={8}>
    <Card size="small">
      <Statistic 
        title="I.V.A. (después dtos)" 
        value={factura.resumen?.totalIVA || 0} 
        precision={2} 
        suffix="€" 
        valueStyle={{ color: '#cf1322' }}
      />
    </Card>
  </Col>
  <Col span={8}>
    <Card size="small" style={{ background: '#f6ffed' }}>
      <Statistic 
        title="TOTAL (IVA inc.)" 
        value={factura.resumen?.totalGeneral || 0} 
        precision={2} 
        suffix="€" 
        valueStyle={{ color: '#3f8600', fontWeight: 'bold' }}
      />
    </Card>
  </Col>
</Row>

      {/* Entregas a cuenta y pendiente (como en FastReport) */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Entregado a Cuenta:</Text>
            <br />
            <Text strong style={{ fontSize: 16 }}>
              {parseFloat(factura.resumen?.aCuenta || 0).toFixed(2)} €
            </Text>
          </Col>
          <Col span={12}>
            <Text type="secondary">Total a pagar:</Text>
            <br />
            <Text strong style={{ fontSize: 16, color: '#fa8c16' }}>
              {parseFloat(factura.resumen?.pendiente || 0).toFixed(2)} €
            </Text>
          </Col>
        </Row>
        <Divider style={{ margin: '12px 0' }} />
        <Text type="secondary">Observaciones:</Text>
        <br />
        <Text>{factura.resumen?.observaciones || factura.datosReserva?.observaciones || 'Sin observaciones'}</Text>
      </Card>

      {/* Descuentos si existen (como en FastReport) */}
      {(factura.resumen?.descuentos || []).length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Text strong>DESCUENTOS APLICADOS</Text>
          <List
            size="small"
            dataSource={factura.resumen.descuentos}
            renderItem={(item) => (
              <List.Item>
                <Text>{item.descripcion}: {item.porcentaje}% = {item.importe.toFixed(2)} €</Text>
              </List.Item>
            )}
          />
        </Card>
      )}
    </>
  );

  return (
    <Modal
      title={
        <Space>
          <FileDoneOutlined />
          <span>Facturación - Reserva {reserva?.NumReserva}</span>
          {reserva?.Nombre && <Text type="secondary">({reserva.Nombre})</Text>}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cerrar
        </Button>,
        <Button 
          key="proforma" 
          icon={<FileTextOutlined />}
          onClick={() => generarDocumento('proforma')}
          loading={generando && tipoDocumento === 'proforma'}
        >
          Regenerar Proforma
        </Button>,
        <Button 
          key="print" 
          icon={<PrinterOutlined />}
          onClick={() => window.print()}
          disabled={!factura}
        >
          Imprimir
        </Button>,
        <Button 
          key="factura" 
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleEmitirFactura}
          loading={generando && tipoDocumento === 'factura'}
          disabled={factura?.tipo === 'factura'}
        >
          Emitir Factura
        </Button>
      ]}
      style={{ top: 0 }} // Esto lo pega al top
        
      bodyStyle={{ 
        maxHeight: 'calc(100vh - 50px)', 
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: '#fff'
      }}
    >
      {/* Configuración superior (similar a FastReport) */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <DatePicker
              value={fechaFactura}
              onChange={setFechaFactura}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              placeholder="Fecha factura"
            />
          </Col>
          <Col span={8}>
            <Select value={tipoDocumento} onChange={setTipoDocumento} style={{ width: '100%' }}>
              <Option value="proforma">Proforma</Option>
              <Option value="factura">Factura</Option>
            </Select>
          </Col>
          <Col span={8}>
            {historialFacturas.length > 0 && (
              <Alert
                message={`${historialFacturas.length} factura(s) emitida(s)`}
                type="info"
                showIcon
                size="small"
              />
            )}
          </Col>
        </Row>
      </Card>

      {/* Contenido principal */}
      {generando ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <CalculatorOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4}>Generando {tipoDocumento}...</Title>
          <Text type="secondary">Procesando datos de la reserva</Text>
        </div>
      ) : (
        contenidoFactura
      )}

      {/* Historial de facturas (similar a FastReport) */}
      {historialFacturas.length > 0 && (
        <Card title="Historial de Facturas" size="small" style={{ marginTop: 16 }}>
          <Table
            dataSource={historialFacturas}
            columns={[
              { title: 'Número', dataIndex: 'Numero', key: 'Numero', width: 80 },
              { title: 'Serie', dataIndex: 'Serie', key: 'Serie', width: 60 },
              { 
                title: 'Fecha', 
                dataIndex: 'Fecha', 
                key: 'Fecha', 
                width: 100,
                render: (fecha) => dayjs(fecha).format('DD/MM/YYYY') 
              },
              { 
                title: 'Total', 
                dataIndex: 'Total', 
                key: 'Total', 
                width: 100,
                render: (total) => `${parseFloat(total || 0).toFixed(2)} €`,
                align: 'right'
              },
              { 
                title: 'Estado', 
                dataIndex: 'Estado', 
                key: 'Estado', 
                width: 80,
                render: (estado) => <Tag color="green">{estado}</Tag> 
              }
            ]}
            size="small"
            pagination={false}
            rowKey="Id"
          />
        </Card>
      )}
    </Modal>
  );
};

export default Facturacion;